import React, { useState, useEffect } from "react"
import { Box, Checkbox, FormControlLabel, Grid, Typography, makeStyles, useTheme } from "@material-ui/core"
import { useDispatch, useSelector } from "react-redux"
import { BasicTable } from "../../common/Table"
import { InfoOutlined } from "@material-ui/icons"
import ColorLegend from "./ColorLegend"
import { operationsHelper } from "../../../helpers/operations"
import {
	changeTermTarget,
	changeIgnoreRelated,
	changeIncludeSpecific,
	changeIgnoreSpecific,
	setUnsaveFlag,
	setHighlightConcepts,
	setSearchTerms,
} from "../../../actions/buildconceptActions"
import { useTranslation } from "react-i18next"

const useStyles = makeStyles((theme) => ({
	table: {
		marginBottom: 82,
		"& .MuiDataGrid-row .MuiDataGrid-cell:first-child": {
			paddingLeft: 0,
		},
	},
	emailDialogBox: {
		"& p": {
			paddingBottom: 2,
			...theme.typography.body2,
		},
		"& h6": {
			paddingBottom: 4,
			...theme.typography.body2,
			fontSize: "12px",
		},
	},
	hitmap: {
		width: 6,
		height: 50,
		borderRadius: "0 5px 5px 0px",
	},
	infoIcon: {
		width: 13,
		height: 13,
		cursor: "pointer",
	},
	termWrapper: {
		display: "flex",
		alignItems: "center",
	},
	termName: {
		marginLeft: 10,
	},
	addToSearchAll: {
		display: "flex",
		alignItems: "center",
		marginLeft: -6,
		"& .MuiTypography-root": {
			fontWeight: 500,
		},
	},
	termNameTypo: {
		...theme.typography.body2,
	},
}))

const ConceptsList = ({ currentSearchTermsSelected, setCurrentSearchTermsSelected }) => {
	const classes = useStyles()
	const theme = useTheme()
	const [t] = useTranslation("global")

	const [rows, setRows] = useState([])
	const [addToSearchTerms, setAddToSearchTerms] = useState([])

	/*ColorLegend setup start */
	const [anchorElColorLegend, setAnchorElColorLegend] = useState(null)
	const openColorLegend = Boolean(anchorElColorLegend)
	const ColorLegendId = openColorLegend ? "color-legend-popover" : undefined
	const handleOpenColorLegend = (event) => {
		setAnchorElColorLegend(event.currentTarget)
	}
	/*ColorLegend setup end*/

	const TargetTerm = (params) => {
		const lowerCaseTargetTerms = targetTerms?.map((term) => term?.toLowerCase())
		const isChecked = lowerCaseTargetTerms?.includes(params?.row?.id)

		const handleChange = (event) => {
			changeTargetTerm(event?.target?.value, event?.target?.checked)
		}

		return (
			<Checkbox
				color="secondary"
				inputProps={{ "aria-label": "secondary checkbox" }}
				checked={isChecked}
				value={params?.row?.id}
				onChange={handleChange}
			/>
		)
	}

	const addToSearchAll = () => {
		const isChecked = searchTerms?.length === 0 ? true : false

		const handleChange = (event) => {
			const updatedState = event?.target?.checked ? [] : rows.map((row) => row?.id)
			dispatch(setSearchTerms(updatedState))
		}

		return (
			<Box className={classes.addToSearchAll}>
				{rows?.length !== 0 && (
					<Checkbox
						color="secondary"
						inputProps={{ "aria-label": "secondary checkbox" }}
						checked={isChecked}
						value={"all"}
						onChange={handleChange}
					/>
				)}
				<Typography variant="body2">{t("concept.list_columns.add_to_search")}</Typography>
			</Box>
		)
	}

	const RemoveFromSearchAll = () => {
		const isChecked = searchTerms?.length === rows?.length ? true : false

		const handleChange = (event) => {
			const updatedState = event?.target?.checked ? rows.map((row) => row?.id) : []
			dispatch(setSearchTerms(updatedState))
		}

		return (
			<Box className={classes.addToSearchAll}>
				{rows?.length !== 0 && (
					<Checkbox
						color="secondary"
						inputProps={{ "aria-label": "secondary checkbox" }}
						checked={isChecked}
						value={"all"}
						onChange={handleChange}
					/>
				)}
				<Typography variant="body2">{t("concept.list_columns.add_to_search")}</Typography>
			</Box>
		)
	}

	const AddToSearch = (params) => {
		const isChecked = !searchTerms?.includes(params?.row?.id)

		const handleChange = (event) => {
			const updatedState = event?.target?.checked
				? searchTerms?.filter((term) => term !== event?.target?.value)
				: [...searchTerms, event?.target?.value]
			dispatch(setSearchTerms(updatedState))
		}

		return (
			<Checkbox
				color="secondary"
				inputProps={{ "aria-label": "secondary checkbox" }}
				checked={isChecked}
				value={params?.row?.id}
				onChange={handleChange}
			/>
		)
	}

	const RemoveFromSearch = (params) => {
		const isChecked = searchTerms?.includes(params?.row?.id)

		const handleChange = (event) => {
			const updatedState = event?.target?.checked
				? [...searchTerms, event?.target?.value]
				: searchTerms?.filter((term) => term !== event?.target?.value)
			dispatch(setSearchTerms(updatedState))
		}

		return (
			<Checkbox
				color="secondary"
				inputProps={{ "aria-label": "secondary checkbox" }}
				checked={isChecked}
				value={params?.row?.id}
				onChange={handleChange}
			/>
		)
	}

	const getColumns = () => {
		return [
			{
				field: "term",
				sortable: false,
				resizable: true,
				flex: 1,
				width: "40%",
				renderHeader: () => (
					<Box>
						{t("concept.list_columns.terms")} <InfoOutlined className={classes.infoIcon} onClick={handleOpenColorLegend} />
						<ColorLegend
							anchorEl={anchorElColorLegend}
							setAnchorEl={setAnchorElColorLegend}
							open={openColorLegend}
							id={ColorLegendId}
						/>
					</Box>
				),
				renderCell: (params) => (
					<Box className={classes.termWrapper}>
						<div className={classes.hitmap} style={{ backgroundColor: params?.row?.color }}></div>
						<div className={classes.termName}>
							<Typography
								className={classes.termNameTypo}
								variant="subtitle1"
								style={searchTerms?.includes(params?.row?.term) ? { color: theme.palette.grey["D1600"] } : { color: "" }}
							>
								{params?.row?.term}
							</Typography>
						</div>
					</Box>
				),
			},
			{
				field: "hits",
				headerName: t("concept.list_columns.hits"),
				headerAlign: "right",
				sortable: false,
				flex: 0.3,
				width: "30%",
				align: "right",
			},
			{
				field: "target_term",
				headerName: t("concept.list_columns.target_terms"),
				sortable: false,
				renderCell: (params) => TargetTerm(params),
				flex: 0.3,
				width: "15%",
			},
			{
				field: "add_to_search",
				headerName: "",
				sortable: false,
				flex: 0.3,
				width: "15%",
				renderHeader: () => RemoveFromSearchAll(),
				renderCell: (params) => RemoveFromSearch(params),
			},
		]
	}

	const getRows = () => {
		return rows
	}

	/* Old code heatmap logic start */
	const dispatch = useDispatch()
	const targetTerms = useSelector((state) => state.buildconcept.targetTerms)
	const isRefreshTermList = useSelector((state) => state.buildconcept.isRefreshTermList)
	const searchTerms = useSelector((state) => state.buildconcept.searchTerms)
	const ignoreTerms = useSelector((state) => state.buildconcept.ignoreTerms)
	const includeSpecificTerms = useSelector((state) => state.buildconcept.includeSpecificTerms)
	const ignoreSpecificTerms = useSelector((state) => state.buildconcept.ignoreSpecificTerms)
	const topicResult = useSelector((state) => state.buildconcept.topicBuilderResults)
	const currentNeg = useSelector((state) => state.buildconcept.currentFilter.neg_phrase_count)
	const loadedTerms = useSelector((state) => state.buildconcept.loadedAPIData.visual)
	const phraseCount = useSelector((state) => state.buildconcept?.currentFilter?.phrase_count || 0)
	const isLoading = useSelector((state) => state.buildconcept.loading)
	/* ******************* END REDUX STORE ******************* */

	/* ******************* REACT STATES ******************* */
	const [currentTerms_targetTerms, setCurrentTerms_targetTerms] = useState(targetTerms)
	const [currentTerms_ignoreTerms, setCurrentTerms_ignoreTerms] = useState(ignoreTerms)
	const [currentTerms_includeSpecificTerms, setCurrentTerms_includeSpecificTerms] = useState(includeSpecificTerms)
	const [currentTerms_ignoreSpecificTerms, setCurrentTerms_ignoreSpecificTerms] = useState(ignoreSpecificTerms)

	const [checkbox, setCheckbox] = useState({
		targetTerm: "",
		ignoreRelated: "",
		includeSpecific: "",
		ignoreSpecific: "",
	})
	const [sortTopicResult, setSortTopicResult] = useState([])
	const [relationessSortTopicResult, setRelationessSortTopicResult] = useState([])
	const [normalizedSortTopicResult, setNormalizedSortTopicResult] = useState([])
	const [tableKey, setTableKey] = useState(Math.random())

	const [showHeatmapColors, setShowHeatmapColors] = useState(true)
	const [heatMapResult, setHeatMapResult] = useState({})

	const changeTargetTerm = (term, checked) => {
		if (checked === true) {
			dispatch(changeTermTarget(term, true))
		} else {
			dispatch(changeTermTarget(term, false))
		}
	}

	//Select current term as Ignore Related
	const changeIgnoreRelatedState = (term, checked) => {
		if (checked === true) {
			dispatch(changeTermTarget(term, false))
			dispatch(changeIgnoreRelated(term, true))
			dispatch(changeIncludeSpecific(term, false))
			dispatch(changeIgnoreSpecific(term, false))
		} else {
			dispatch(changeIgnoreRelated(term, false))
		}
		//dispatch(setUnsaveFlag(true))
	}

	//Select current term as Inclide Specific
	const changeIncludeSpecificState = (term, checked) => {
		if (checked === true) {
			dispatch(changeTermTarget(term, false))
			dispatch(changeIgnoreRelated(term, false))
			dispatch(changeIncludeSpecific(term, true))
			dispatch(changeIgnoreSpecific(term, false))
		} else {
			dispatch(changeIncludeSpecific(term, false))
		}
		//dispatch(setUnsaveFlag(true))
	}

	//Select current term as Ignore Specific
	const changeIgnoreSpecificState = (term, checked) => {
		if (checked === true) {
			dispatch(changeTermTarget(term, false))
			dispatch(changeIgnoreRelated(term, false))
			dispatch(changeIncludeSpecific(term, false))
			dispatch(changeIgnoreSpecific(term, true))
		} else {
			dispatch(changeIgnoreSpecific(term, false))
		}
		//dispatch(setUnsaveFlag(true))
	}

	useEffect(() => {
		if (loadedTerms !== undefined) {
			setCurrentSearchTermsSelected({
				target_terms: loadedTerms.terms,
				ignore_terms: loadedTerms.ignore,
				soft_ignore: loadedTerms.soft_ignore,
				soft_target: loadedTerms.soft_include,
			})
		}
	}, [loadedTerms])

	useEffect(() => {
		setTableKey(Math.random())
	}, [currentSearchTermsSelected])

	const renderTable = () => {
		let auxTarget = topicResult.filter((i) => targetTerms.includes(i[1]))
		let minColorAux = Math.max.apply(
			null,
			auxTarget.map((i) => i[2])
		)
		auxTarget = auxTarget.concat(topicResult.filter((i) => includeSpecificTerms.includes(i[1])))
		let auxExclude = topicResult.filter((i) => ignoreTerms.includes(i[1]))
		auxExclude = auxExclude.concat(topicResult.filter((i) => ignoreSpecificTerms.includes(i[1])))
		let maxColorAux = Math.min.apply(
			null,
			auxExclude.map((i) => i[2])
		)

		let listWithExcludes = topicResult.filter((i) => !auxTarget.includes(i))
		listWithExcludes = listWithExcludes.filter((i) => !auxExclude.includes(i))
		let positiveListWithExcludes = listWithExcludes.filter((i) => i[2] >= 0)
		let negativeListWithExcludes = listWithExcludes.filter((i) => i[2] < 0)

		for (var i = 0; i < auxTarget.length; i++) {
			if (auxTarget[i][2] < minColorAux) {
				auxTarget[i][2] = minColorAux
			}
		}
		for (var i = 0; i < auxExclude.length; i++) {
			if (auxExclude[i][2] > maxColorAux) {
				auxExclude[i][2] = maxColorAux
			}
		}
		const topicResultData = auxTarget.concat(positiveListWithExcludes).concat(auxExclude).concat(negativeListWithExcludes)
		setSortTopicResult(topicResultData)
	}
	useEffect(() => {
		renderTable()
	}, [topicResult])

	useEffect(() => {
		setRelationessSortTopicResult(sortTopicResult.map((i) => i[2]))
		setNormalizedSortTopicResult(operationsHelper.normalize_array(relationessSortTopicResult))
	}, [sortTopicResult])

	useEffect(() => {
		var negativeItems = document.getElementsByClassName("negative_term")
		for (var i = 0; i < negativeItems.length; i++) {
			if (i + 1 <= currentNeg) {
				negativeItems[i].classList.remove("hideNegative")
			} else {
				negativeItems[i].classList.add("hideNegative")
			}
		}

		let maxValuePositive = operationsHelper.maxRelatednessPositive(sortTopicResult)
		let minValuePositive = operationsHelper.minRelatednessPositive(sortTopicResult)
		let maxValueNegative = operationsHelper.maxRelatednessNegative(sortTopicResult)
		let minValueNegative = operationsHelper.minRelatednessNegative(sortTopicResult)

		//Modify the list of terms searching for
		let positiveSearch = sortTopicResult.filter((i) => i[2] >= 0).map((i) => `${i[1]}`)
		let negativeSearch = sortTopicResult
			.filter((i) => i[2] < 0)
			.slice(0, currentNeg)
			.map((i) => `${i[1]}`)
		dispatch(setHighlightConcepts({ positive: positiveSearch, negative: negativeSearch }))
	}, [currentNeg, sortTopicResult])

	useEffect(() => {
		heatMapCalculation()
	}, [sortTopicResult, showHeatmapColors])

	useEffect(() => {
		setCurrentTerms_targetTerms(targetTerms)
		//renderTable()
		return
	}, [targetTerms])
	useEffect(() => {
		setCurrentTerms_ignoreTerms(ignoreTerms)
		return
	}, [ignoreTerms])
	useEffect(() => {
		setCurrentTerms_includeSpecificTerms(includeSpecificTerms)
		return
	}, [includeSpecificTerms])
	useEffect(() => {
		setCurrentTerms_ignoreSpecificTerms(ignoreSpecificTerms)
		return
	}, [ignoreSpecificTerms])

	useEffect(() => {
		const rowsData = sortTopicResult?.length
			? sortTopicResult?.map((item) => ({
					id: item[1],
					term: item[1],
					hits: item[0],
					target_term: targetTerms.indexOf(item[1]) !== -1 ? true : false,
					search_terms: true,
					color: heatMapResult.hasOwnProperty(item[1]) ? heatMapResult[item[1]] : null,
			  }))
			: []

		setRows((prev) => rowsData)
		const updatedAddToSearchTerms = searchTerms?.filter((term) => rowsData?.id !== term)
		dispatch(setSearchTerms(updatedAddToSearchTerms))
	}, [heatMapResult])

	const heatMapCalculation = (conceptList) => {
		let auxHeat = {}
		if (showHeatmapColors) {
			let selectedTerms =
				currentSearchTermsSelected.target_terms === null || currentSearchTermsSelected.target_terms === undefined
					? []
					: currentSearchTermsSelected.target_terms
			let selectedSoft =
				currentSearchTermsSelected.soft_target === null || currentSearchTermsSelected.soft_target === undefined
					? []
					: currentSearchTermsSelected.soft_target
			let selectExclude =
				currentSearchTermsSelected.ignore_terms === null || currentSearchTermsSelected.ignore_terms === undefined
					? []
					: currentSearchTermsSelected.ignore_terms
			let selectSoftIgnore =
				currentSearchTermsSelected.soft_ignore === null || currentSearchTermsSelected.soft_ignore === undefined
					? []
					: currentSearchTermsSelected.soft_ignore

			selectedTerms.map((term) => {
				auxHeat[term] = "#222222"
			})
			selectedSoft.map((term) => {
				auxHeat[term] = "#222222"
			})
			selectExclude.map((term) => {
				auxHeat[term] = "#E1E1E1"
			})
			selectSoftIgnore.map((term) => {
				auxHeat[term] = "#E1E1E1"
			})
			let negatives = JSON.parse(JSON.stringify(sortTopicResult))
			negatives
				.filter((item) => item[2] < 0)
				.map((term) => {
					auxHeat[term[1]] = "#E1E1E1"
				})
			let clone = JSON.parse(JSON.stringify(sortTopicResult))
			clone = clone.filter((item) => {
				return Object.keys(auxHeat).indexOf(item[1]) === -1
			})

			clone.map((item) => {
				let numero_menores = clone.filter((item2) => item2[2] <= item[2]).length
				let percentil = numero_menores / clone.length
				auxHeat[item[1]] = "hsl(" + (206 - percentil * 206) + ", 100%, 50%)"
			})
		} else {
			sortTopicResult.map((item) => {
				auxHeat[item[1]] = "#222222"
			})
		}
		setHeatMapResult(auxHeat)
	}

	/* ******************* VARIABLES ******************* */
	// HeatMap Colours Mapping
	let maxValuePositive = operationsHelper.maxRelatednessPositive(sortTopicResult)
	let minValuePositive = operationsHelper.minRelatednessPositive(sortTopicResult)
	let maxValueNegative = operationsHelper.maxRelatednessNegative(sortTopicResult)
	let minValueNegative = operationsHelper.minRelatednessNegative(sortTopicResult)

	/* ******************* END VARIABLES ******************* */
	/* Old code heatmap logic end */

	return (
		<>
			<Grid container item className={classes.table} justifyContent="space-around" style={{ position: "relative" }}>
				<BasicTable
					columns={getColumns()}
					rows={getRows()}
					isLoading={isLoading}
					pageInfo={{
						page: 1,
						pageSize: rows?.length,
						rowsPerPageOptions: [5, 10, 20, 50],
						totalRowCount: rows.length,
						paginationMode: "server",
						handlePageChange: () => {},
						handlePageSizeChange: () => {},
					}}
					showPagination={false}
					sortInfo={{
						sortingMode: "server",
						sortModel: [],
						onSortModelChange: () => {},
					}}
					checkboxSelection={false}
					footerInfo={{
						hideFooter: false,
						hideSelectionCount: true,
						hidePagination: true,
					}}
					topBarInfo={{
						show: true,
						rightSection: "",
					}}
					totalCountText={`Total Terms ${rows?.length}`}
					showDownloadButton={false}
					showColumnsButton={false}
				/>
			</Grid>
		</>
	)
}

export default ConceptsList
